import React, { createContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLoadingState } from '@/utils/loading';
import { LoadingStateManager } from '@/utils/loading/LoadingStateManager';
import { ViewStateManager } from '../view/ViewStateManager';
import { NavigationManager } from '../navigation/NavigationManager';
import { CacheController } from '../cache/CacheController';
import { ViewState, ViewStateContextType } from '../view/types';
import { NavigationContextType, NavigationOptions } from '../navigation/types';
import { CacheContextType } from '../cache/types';
import BrowserHistoryHandler from '@/components/BrowserHistoryHandler';

// Combined context type
export interface ContentManagerContextType extends 
  ViewStateContextType,
  NavigationContextType,
  CacheContextType {
  isLoading: boolean;
  error: Error | null;
}

// Create context with default values
export const ContentManagerContext = createContext<ContentManagerContextType>({
  // View state defaults
  currentView: null,
  cachedViews: {},
  saveScrollPosition: () => {},
  updateViewState: () => {},
  getViewState: () => undefined,
  clearViewState: () => {},
  
  // Navigation defaults
  loadView: async () => {},
  preloadView: async () => {},
  navigateTo: () => {},
  createDeepLink: () => '',
  parseDeepLinkState: () => ({}),
  
  // Cache defaults
  prefetchRouteData: () => {},
  invalidateSegment: () => {},
  clearCache: () => {},
  prefetchSegment: () => {},
  
  // App state defaults
  isLoading: false,
  error: null
});

interface ContentManagerProviderProps {
  children: ReactNode;
  cacheExpiration?: number;
}

export const ContentManagerProvider = ({ 
  children,
  cacheExpiration = 5 * 60 * 1000 // Default 5 minutes 
}: ContentManagerProviderProps) => {
  // Local state
  const [currentView, setCurrentView] = useState<ViewState | null>(null);
  const [cachedViews, setCachedViews] = useState<Record<string, ViewState>>({});
  const [error, setError] = useState<Error | null>(null);
  
  // Hooks
  const queryClient = useQueryClient();
  const { isLoading: globalLoading } = useLoadingState();
  
  // Managers
  const viewStateManager = ViewStateManager.getInstance();
  const navigationManager = NavigationManager.getInstance(cacheExpiration);
  const cacheController = CacheController.getInstance();
  
  // Initialize cache controller
  useEffect(() => {
    cacheController.setQueryClient(queryClient);
  }, [queryClient]);

  // Subscribe to view state changes
  useEffect(() => {
    return viewStateManager.subscribe((views, current) => {
      setCachedViews(views);
      setCurrentView(current);
    });
  }, []);

  // Function to load a view
  const loadView = useCallback(async (path: string, options?: NavigationOptions) => {
    try {
      await navigationManager.loadView(path, options);
    } catch (err) {
      console.error("Error loading view:", err);
      setError(err instanceof Error ? err : new Error("Failed to load view"));
    }
  }, [navigationManager]);

  // Enhanced navigation function with loading state that works outside router context
  const navigateTo = useCallback((path: string, state?: Record<string, any>) => {
    // Start loading indicator for navigation
    const loadingId = LoadingStateManager.getInstance().addRequest(`navigation:${path}`, 'high');
    
    // Preload the content for this path
    navigationManager.preloadView(path);
    
    // Prefetch with React Query and Cache Controller
    cacheController.prefetchRouteData(path);
    
    // Update the URL and history state using native browser APIs
    // This works even outside of a React Router context
    const url = new URL(window.location.origin + path);
    
    // Preserve any query parameters if needed
    if (state && Object.keys(state).length > 0) {
      // If state should be in the URL, add as query params
      // Otherwise just keep in the history state object
      window.history.pushState({ ...window.history.state, usr: state }, '', url.toString());
    } else {
      window.history.pushState({}, '', url.toString());
    }
    
    // Load the view
    loadView(path, { state });
    
    // Stop loading after navigation
    LoadingStateManager.getInstance().completeRequest(loadingId);
  }, [navigationManager, cacheController, loadView]);

  // Function for preloading a view
  const preloadView = useCallback((path: string) => {
    return navigationManager.preloadView(path);
  }, [navigationManager]);

  // Function to save scroll position
  const saveScrollPosition = useCallback((path: string, position: number) => {
    viewStateManager.saveScrollPosition(path, position);
  }, [viewStateManager]);

  // Function to clear cache
  const clearCache = useCallback((path?: string) => {
    if (path) {
      viewStateManager.clearViewState(path);
      queryClient.invalidateQueries({ queryKey: ['page', path] });
    } else {
      viewStateManager.clearViewState();
      cacheController.clearCache();
    }
  }, [queryClient, viewStateManager, cacheController]);

  // Function to update view state
  const updateViewState = useCallback((path: string, state: Record<string, any>) => {
    viewStateManager.updateViewState(path, state);
  }, [viewStateManager]);

  // Function to get view state
  const getViewState = useCallback((path: string) => {
    return viewStateManager.getViewState(path);
  }, [viewStateManager]);

  // Create a deep link
  const createDeepLink = useCallback((path: string, state: Record<string, any> = {}) => {
    return navigationManager.createDeepLink(path, state);
  }, [navigationManager]);

  // Parse deep link state
  const parseDeepLinkState = useCallback((queryString: string) => {
    return navigationManager.parseDeepLinkState(queryString);
  }, [navigationManager]);

  // Prefetch route data
  const prefetchRouteData = useCallback((path: string) => {
    cacheController.prefetchRouteData(path);
  }, [cacheController]);

  // Invalidate a cache segment
  const invalidateSegment = useCallback((segmentName: string) => {
    cacheController.invalidateSegment(segmentName);
  }, [cacheController]);

  // Prefetch a cache segment
  const prefetchSegment = useCallback((segmentName: string) => {
    cacheController.prefetchSegment(segmentName);
  }, [cacheController]);

  // Save current scroll position when navigating away
  useEffect(() => {
    const handleScroll = () => {
      if (currentView) {
        saveScrollPosition(currentView.path, window.scrollY);
      }
    };

    // Throttled scroll handler
    let timeout: number;
    const throttledScrollHandler = () => {
      if (!timeout) {
        timeout = window.setTimeout(() => {
          handleScroll();
          timeout = 0;
        }, 200);
      }
    };

    window.addEventListener('scroll', throttledScrollHandler);
    return () => window.removeEventListener('scroll', throttledScrollHandler);
  }, [currentView, saveScrollPosition]);

  // Context value
  const contextValue: ContentManagerContextType = {
    // View state
    currentView,
    cachedViews,
    saveScrollPosition,
    updateViewState,
    getViewState,
    clearViewState: clearCache,
    
    // Navigation
    loadView,
    preloadView,
    navigateTo,
    createDeepLink,
    parseDeepLinkState,
    
    // Cache
    prefetchRouteData,
    invalidateSegment,
    clearCache,
    prefetchSegment,
    
    // App state
    isLoading: globalLoading,
    error
  };

  return (
    <ContentManagerContext.Provider value={contextValue}>
      <BrowserHistoryHandler 
        onLocationChange={(path) => loadView(path)}
        prefetchRouteData={prefetchRouteData}
        loadView={loadView}
        cachedViews={cachedViews}
      />
      {children}
    </ContentManagerContext.Provider>
  );
};
